import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  TextField,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useManageExport } from './useManageExport';
import moment from 'moment';
import { toast } from 'react-toastify';

const MonthlyExport = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get month/year from URL or default to current month
  const urlMonth = searchParams.get('month');
  const urlYear = searchParams.get('year');
  const defaultMonth = moment().format('YYYY-MM');
  
  const [selectedMonth, setSelectedMonth] = useState(
    urlMonth && urlYear ? `${urlYear}-${urlMonth.padStart(2, '0')}` : defaultMonth
  );
  
  const month = parseInt(selectedMonth.split('-')[1]);
  const year = parseInt(selectedMonth.split('-')[0]);
  
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMissingData, setHasMissingData] = useState(false);
  
  const {
    exportMonthlyReports,
    exportMonthlyTaxInvoices,
    getMissingDataCustomers,
    getCreditNoteEligibleOrders,
    assignCreditNoteDates,
    exportMonthlyCreditNotes,
    getWhtSummary,
    loading: exportLoading
  } = useManageExport();

  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [assignedNotes, setAssignedNotes] = useState([]);
  const [creditNoteLoading, setCreditNoteLoading] = useState(false);
  const [refundDates, setRefundDates] = useState({});

  const [whtRows, setWhtRows] = useState([]);
  const [whtTotals, setWhtTotals] = useState(null);
  const [whtLoading, setWhtLoading] = useState(false);
  const [whtError, setWhtError] = useState(null);

  const fetchMissingDataCustomers = async () => {
    if (!month || !year) return;
    
    setLoading(true);
    try {
      const data = await getMissingDataCustomers(month, year);
      setCustomers(data);
      setHasMissingData(data && data.length > 0);
    } catch (error) {
      toast.error('Failed to fetch missing data customers');
      console.error(error);
      setCustomers([]);
      setHasMissingData(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditNoteOrders = async () => {
    if (!month || !year) return;
    setCreditNoteLoading(true);
    try {
      const data = await getCreditNoteEligibleOrders(month, year);
      const unassigned = data?.unassigned || [];
      const assigned = data?.assigned || [];
      setUnassignedOrders(unassigned);
      setAssignedNotes(assigned);
      const initialDates = {};
      unassigned.forEach((o) => { initialDates[String(o.orderId)] = ''; });
      setRefundDates(initialDates);
    } catch (error) {
      console.error(error);
      setUnassignedOrders([]);
      setAssignedNotes([]);
      setRefundDates({});
    } finally {
      setCreditNoteLoading(false);
    }
  };

  const fetchWhtSummary = async () => {
    if (!month || !year) return;
    setWhtLoading(true);
    setWhtError(null);
    try {
      const data = await getWhtSummary(month, year);
      setWhtRows(data?.rows || []);
      setWhtTotals(data?.totals || null);
    } catch (error) {
      console.error('[WHT] fetch failed', error);
      setWhtError(error?.response?.data?.message || error?.message || 'Failed to load WHT summary');
      setWhtRows([]);
      setWhtTotals(null);
    } finally {
      setWhtLoading(false);
    }
  };

  const buildWhtTsv = () => {
    const header = [
      'เลขที่ใบกำกับภาษี', 'วันที่', 'ลูกค้า', 'เลขผู้เสียภาษี',
      'ค่าบริการก่อน VAT', 'หักใบลดหนี้', 'อ้างอิงใบลดหนี้', 'ค่าบริการสุทธิ', 'หัก ณ ที่จ่าย 3%',
    ];
    const lines = whtRows.map((r) => [
      r.taxInvoiceNo,
      r.date,
      r.customerName,
      r.customerTaxNo,
      r.serviceFeeBeforeTax,
      r.creditedAmount,
      r.creditNoteRef,
      r.netServiceFee,
      r.whtAmount,
    ].join('\t'));
    return [header.join('\t'), ...lines].join('\n');
  };

  const handleCopyWht = async () => {
    if (whtRows.length === 0) {
      toast.info('ไม่มีข้อมูลให้คัดลอก');
      return;
    }
    try {
      await navigator.clipboard.writeText(buildWhtTsv());
      toast.success('คัดลอกแล้ว — วางใน Google Sheets ได้เลย');
    } catch (error) {
      console.error(error);
      toast.error('คัดลอกไม่สำเร็จ');
    }
  };

  const handleRefundDateChange = (orderId, value) => {
    setRefundDates((prev) => ({ ...prev, [String(orderId)]: value }));
  };

  const handleAssignSingle = async (order) => {
    const dateVal = refundDates[String(order.orderId)];
    if (!dateVal) {
      toast.error('กรุณากรอกวันที่คืนเงินก่อน');
      return;
    }
    if (!order.taxInvoiceCreated) {
      toast.error('กรุณา Export ใบกำกับภาษีก่อน');
      return;
    }
    try {
      const result = await assignCreditNoteDates(month, year, { [String(order.orderId)]: dateVal });
      if (result === true) {
        toast.success(`บันทึก ${order.orderCode} เรียบร้อย`);
        fetchCreditNoteOrders();
      } else {
        toast.error(`${result}`);
      }
    } catch (error) {
      toast.error('Failed to assign credit note');
      console.error(error);
    }
  };

  const handleExportCreditNotes = async () => {
    try {
      const result = await exportMonthlyCreditNotes(month, year);
      if (result === true) {
        toast.success('Credit Notes exported successfully!');
      } else {
        toast.error(`Failed to export credit notes: ${result}`);
      }
    } catch (error) {
      toast.error('Failed to export credit notes');
      console.error(error);
    }
  };

  const handleExportMonthlyReports = async () => {
    try {
      const result = await exportMonthlyReports(month, year);
      if (result === true) {
        toast.success('Monthly reports exported successfully!');
      } else {
        toast.error(`Failed to export monthly reports: ${result}`);
      }
    } catch (error) {
      toast.error('Failed to export monthly reports');
      console.error(error);
    }
  };

  const handleExportMonthlyTaxInvoices = async () => {
    try {
      const result = await exportMonthlyTaxInvoices(month, year);
      if (result === true) {
        toast.success('Monthly tax invoices exported successfully!');
      } else {
        toast.error(`Failed to export monthly tax invoices: ${result}`);
      }
    } catch (error) {
      toast.error('Failed to export monthly tax invoices');
      console.error(error);
    }
  };

  const handleRowClick = (customer) => {
    // Open customer profile in new tab with smileName query parameter
    const url = `/customer-profile?smileName=${encodeURIComponent(customer.smileName)}`;
    window.open(url, '_blank');
  };

  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);
    
    // Update URL with new month/year
    const [newYear, newMonthNum] = newMonth.split('-');
    setSearchParams({
      month: newMonthNum,
      year: newYear
    });
  };

  const getShopChipColor = (shop) => {
    switch (shop) {
      case 'smile':
        return 'primary'; // Using primary instead of indigo
      case 'tama':
        return 'warning'; // light brown equivalent in MUI
      default:
        return 'default';
    }
  };

  useEffect(() => {
    fetchMissingDataCustomers();
    fetchCreditNoteOrders();
    fetchWhtSummary();
  }, [month, year]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
          Monthly Export & Missing Data
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Month Selector */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="month-select-label">เดือน</InputLabel>
            <Select
              labelId="month-select-label"
              id="month-select"
              value={selectedMonth}
              onChange={handleMonthChange}
              label="เดือน"
            >
              {Array.from({ length: 12 }, (_, i) => moment().subtract(i, 'months')).map((m) => (
                <MenuItem key={m.format('YYYY-MM')} value={m.format('YYYY-MM')}>
                  {m.format('MMMM YYYY')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Refresh Button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={fetchMissingDataCustomers}
            disabled={loading}
          >
            รีเฟรช
          </Button>

          {/* Export Monthly Reports Button */}
          <Button
            variant="contained"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportMonthlyReports}
            //disabled={exportLoading || hasMissingData}
          >
            {exportLoading ? 'กำลัง Export...' : 'Export Monthly Reports'}
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportMonthlyTaxInvoices}
            disabled={exportLoading}
          >
            {exportLoading ? 'กำลัง Export...' : 'Test Download Tax Invoices'}
          </Button>
        </Box>
      </Box>

      {/* Missing Data Alert */}
      {hasMissingData && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          พบข้อมูลลูกค้าที่ไม่ครบ {customers.length} รายการ - กรุณาแก้ไขก่อนทำการ Export รายงานเดือน
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {/* Missing Data Table */}
          {hasMissingData && (
            <>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'warning.main' }}>
                รายชื่อลูกค้าข้อมูลไม่ครบ
              </Typography>
              <TableContainer component={Paper} elevation={3} sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>ชื่อ Smile</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Shop</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow
                        key={customer.id}
                        onClick={() => handleRowClick(customer)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      >
                        <TableCell>{customer.id}</TableCell>
                        <TableCell>
                          <Chip label={customer.smileName} color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>{customer.contact}</TableCell>
                        <TableCell>
                          <Chip 
                            label={customer.shop} 
                            color={getShopChipColor(customer.shop)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Summary */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {hasMissingData 
                ? `พบข้อมูลไม่ครบ: ${customers.length} รายการ` 
                : 'ไม่พบข้อมูลลูกค้าที่ไม่ครบ - สามารถ Export รายงานเดือนได้'
              }
            </Typography>
          </Box>
        </>
      )}

      {/* Credit Note Section */}
      <Divider sx={{ my: 4 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          ใบลดหนี้ / Credit Note
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchCreditNoteOrders}
          disabled={creditNoteLoading}
          size="small"
        >
          รีเฟรช
        </Button>
      </Box>

      {creditNoteLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <>
          {/* Unassigned — need date input */}
          {unassignedOrders.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 1 }}>
                รอกำหนดวันที่คืนเงิน ({unassignedOrders.length} รายการ)
              </Typography>
              <TableContainer component={Paper} elevation={2}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Order Code</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>วันที่สั่ง</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>ลูกค้า</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>ใบกำกับภาษี</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Ref Invoice</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">ยอดรวม (บาท)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>URL</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>วันที่คืนเงิน</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unassignedOrders.map((order, idx) => (
                      <TableRow key={order.orderId} sx={{ '&:hover': { backgroundColor: '#fff8e1' } }}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{order.orderCode}</TableCell>
                        <TableCell>{order.orderDate}</TableCell>
                        <TableCell>{order.customerName}</TableCell>
                        <TableCell>
                          {order.taxInvoiceCreated ? (
                            <Chip label={order.taxInvoiceNo} size="small" color="primary" variant="outlined" />
                          ) : (
                            <Chip label="ยังไม่ได้ออก" size="small" color="error" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>{order.referenceInvoiceNo}</TableCell>
                        <TableCell align="right">
                          {Number(order.totalAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          {order.url && order.url !== '-' ? (
                            <a href={order.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                              Link
                            </a>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            size="small"
                            value={refundDates[String(order.orderId)] || ''}
                            onChange={(e) => handleRefundDateChange(order.orderId, e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            disabled={exportLoading || !refundDates[String(order.orderId)]}
                            onClick={() => handleAssignSingle(order)}
                          >
                            บันทึก
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Assigned — ready to export */}
          {assignedNotes.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  บันทึกแล้ว ({assignedNotes.length} รายการ)
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportCreditNotes}
                  disabled={exportLoading}
                  size="small"
                >
                  {exportLoading ? 'กำลัง Export...' : 'Export Credit Notes'}
                </Button>
              </Box>
              <TableContainer component={Paper} elevation={2}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>เลขที่ใบลดหนี้</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>ใบกำกับภาษี</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>ลูกค้า</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Ref Invoice</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">ยอดรวม (บาท)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>วันที่คืนเงิน</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignedNotes.map((note, idx) => (
                      <TableRow key={note.orderId} sx={{ '&:hover': { backgroundColor: '#f1f8e9' } }}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <Chip label={note.creditNoteNo} size="small" color="success" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={note.taxInvoiceNo} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>{note.customerName}</TableCell>
                        <TableCell>{note.referenceInvoiceNo}</TableCell>
                        <TableCell align="right">
                          {Number(note.totalAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>{note.refundDate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {unassignedOrders.length === 0 && assignedNotes.length === 0 && (
            <Alert severity="info">
              ไม่พบออเดอร์ที่ถูกยกเลิกสำหรับลูกค้าใบกำกับภาษี ในเดือนนี้
            </Alert>
          )}
        </>
      )}

      {/* Withholding Tax (WHT 3%) Section */}
      <Divider sx={{ my: 4 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          ภาษีหัก ณ ที่จ่าย 3% / WHT
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchWhtSummary}
            disabled={whtLoading}
            size="small"
          >
            รีเฟรช
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyWht}
            disabled={whtLoading || whtRows.length === 0}
            size="small"
          >
            คัดลอกไป Google Sheets
          </Button>
        </Box>
      </Box>

      {whtLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : whtError ? (
        <Alert severity="error">
          โหลดข้อมูลไม่สำเร็จ: {whtError}
        </Alert>
      ) : whtRows.length === 0 ? (
        <Alert severity="info">
          ไม่พบใบกำกับภาษีสำหรับเดือนนี้
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>เลขที่ใบกำกับภาษี</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>วันที่</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>ลูกค้า</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>เลขผู้เสียภาษี</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">ค่าบริการก่อน VAT</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">หักใบลดหนี้</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>อ้างอิงใบลดหนี้</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">ค่าบริการสุทธิ</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">หัก ณ ที่จ่าย 3%</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {whtRows.map((row, idx) => (
                <TableRow
                  key={`${row.taxInvoiceNo}-${idx}`}
                  sx={{ backgroundColor: row.creditedAmount > 0 ? '#fff8e1' : 'inherit' }}
                >
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{row.taxInvoiceNo}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{row.customerName}</TableCell>
                  <TableCell>{row.customerTaxNo}</TableCell>
                  <TableCell align="right">
                    {Number(row.serviceFeeBeforeTax).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="right">
                    {row.creditedAmount > 0
                      ? `-${Number(row.creditedAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '-'}
                  </TableCell>
                  <TableCell>{row.creditNoteRef || '-'}</TableCell>
                  <TableCell align="right">
                    {Number(row.netServiceFee).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="right">
                    {Number(row.whtAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
              ))}
              {whtTotals && (
                <TableRow sx={{ backgroundColor: '#f5f5f5', '& td': { fontWeight: 'bold' } }}>
                  <TableCell colSpan={5} align="right">รวมทั้งหมด</TableCell>
                  <TableCell align="right">
                    {Number(whtTotals.serviceFeeBeforeTax).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="right">
                    {`-${Number(whtTotals.creditedAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </TableCell>
                  <TableCell />
                  <TableCell align="right">
                    {Number(whtTotals.netServiceFee).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="right">
                    {Number(whtTotals.whtAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MonthlyExport;